// `/letter` IS THE APP'S STEP 7, "REVIEW & EXPORT" (WEB-PARITY-6, R221; docs/WEB_APP_PARITY.md §2.14,
// LT-1 to LT-12; AppealAce/Features/Review/ReviewView.swift). Tyler, 2026-09-29: "the site should replicate exactly what
// the app has at this time".
//
// THE APP'S SECTIONS, IN THE APP'S ORDER (ReviewView.swift:120-224):
//   1  "Your letter is ready": before you export (contact, signature) · you're requesting · the letter paper with "See my
//      Appeal Letter" and "I need a change to this letter" · worth a second look · the filing deadline;
//   2  "Copy out and file": the appeal letter and "Copy letter as text" · the portal summary;
//   3  "After you've filed": how to file · the outcome · previously exported · delete this appeal.
//
// ONE DOOR TO CHANGE THE LETTER, TWO PATHS (WALK-FIX-4, R243). Tyler, 2026-10-06: "I like theidea of
// turning the I need a change to this letter being a single button that leads you on two paths.. and lets make it clear
// its there". "I need a change to this letter" is one strong button under the paper, always there on a written letter,
// and it opens a sheet with the app's two ways to change it: the AI change request, and "Revise from your details" —
// which the app keeps first under "Copy out and file", and which the website has moved here (`./reviewParts.tsx`
// `ChangeSheet`). "Copy out and file" keeps only the copy-out rows. A website ahead of the app, on Tyler's word, until
// the app follows (docs/WEB_READINESS.md, WALK-FIX-4).
//
// NOTHING HERE DECIDES. The letter, its summary, the audit's findings, the paper's Re: line and chips, whether the
// letterhead is complete, what the generation was queued with, the answer to a change request in the app's sentences,
// the past letters and whether "Email me a copy" is on are all `fetch`'s (functions/src/v2/letter/review.ts); "You're
// requesting" and the outcome's savings are `v2Outcomes`'s summary; who the letter is filed with and the deadline are the
// resolution's. The words around them are the app's (./reviewWording.ts).
//
// NO HAND EDIT OF THE LETTER (D1, LT-3). The app has none — its "Tap to read & edit" row edits nothing — so neither does
// this page: the owner asks for an AI change ("I need a change to this letter", 20 per appeal), revises from their
// details, or copies the text out. `test/letterReview.test.tsx` fails if a field that edits the letter appears here.
//
// THE SIGNATURE travels in the request, never stored (./../../app/signature.ts; the server's own test proves its half).
//
// A LETTER STILL BEING WRITTEN IS NEVER "NOT READY" HERE (WEB-RESUME, R242). A generation the server says
// is in flight opens the Building overlay, whatever route led here (the rail's step, a bookmark, Properties); and on an
// earlier letter's Review, a NEWER generation the appeal is still waiting on — a change request, or a rebuild the owner
// left mid-way — is shown as the app shows a change being made ("Making your changes…") and waited on, so "I need a
// change to this letter" cannot start a second one while the first is being written.

import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import type { FetchLetterResponse, LetterJobView, LetterReview, ResolvePropertyResponse } from "@contract";
import { capturesFinalValue } from "@v2-outcome-rules";
import { useReportAppeal } from "../../app/railAppeal";
import { useAppealId, useSession } from "../../app/session";
import { recordKeepingCorrections } from "../../app/corrections";
import { savePdf } from "../../app/savePdf";
import { forgetSignature, releaseStoredSignature, useSignature } from "../../app/signature";
import { contactIsComplete, ownerOf, useContact } from "../../app/contact";
import { attemptAnswered, attemptKey } from "../../app/letterAttempt";
import { useAction, useCall, type CallState } from "../../app/useCall";
import { AppAlert } from "../../components/AppAlert";
import { CopyLine, HeldLine } from "../../components/copy";
import { heldText } from "../../components/heldCopy";
import { prePayText } from "../../components/prePayCopy";
import { Refusal } from "../../components/Refusal";
import { dollars, money } from "../../format";
import {
  ChangeIcon, ChangeSheet, FailureLine, FeedbackSheet, LetterPaper, PdfSheet, PhaseHeader, ReviewSection, Say, SetupRow,
  SignatureSheet,
} from "./reviewParts";
import { PDF_FOOTER, word, wordWith } from "./reviewWording";
import { ContactSheet } from "./ContactSheet";

/** The app's support address (`supportEmail`, AppealAce/Features/Support/SupportView.swift), for "Email support". */
export const SUPPORT_EMAIL = "support@proppeal.com";

type Shipped = Extract<LetterJobView, { status: "shipped" }>;
type Alert =
  | { kind: "stale" }
  | { kind: "export" }
  | { kind: "delete" }
  | { kind: "exhausted"; max: number }
  | { kind: "concern"; report: NonNullable<LetterReview["concernReport"]> }
  /** `message` null: the change request stopped without a sentence of the server's (WEB-RESUME: a job the server no
   *  longer counts as being written) — the app's title alone. */
  | { kind: "reviseError"; message: string | null };

export function Letter() {
  const [params] = useSearchParams();
  // `/letter` NAMING NO GENERATION (a typed or bookmarked URL) opens the appeal's newest one, as the server's snapshot
  // names it, rather than asking for generation 0, which no appeal has (WALK-FIX-1).
  if (params.get("generationAt") === null) return <NewestLetter />;
  return <LetterAt />;
}

/** Asks the snapshot for the newest generation and goes to it; with none written yet, to the Letter step, step 6. */
function NewestLetter() {
  const session = useSession();
  const account = useCall(() => session.transport.v2Credits.get(), [session.transport, session.revision]);
  if (account.status === "pending") {
    return (
      <p className="spinner-line" role="status">
        Loading your letter…
      </p>
    );
  }
  if (account.status === "failed") return <Refusal failure={account.failure} context="Your letter" />;
  const latest = account.response.appeal?.letter.latestGenerationAt ?? null;
  return <Navigate to={latest === null ? "/you" : `/letter?generationAt=${latest}`} replace />;
}

function LetterAt() {
  const session = useSession();
  const appealId = useAppealId();
  const [params, setParams] = useSearchParams();
  const generationAt = Number(params.get("generationAt") ?? "0");
  // A change request's letter replaces this one in the address, so a reload opens the newest — and its answer is held
  // HERE, above the letter, because the page reloads the letter it names and the answer must outlive that.
  const [concern, setConcern] = useState<NonNullable<LetterReview["concernReport"]> | null>(null);
  const onRevised = useCallback((next: number, report: LetterReview["concernReport"]) => {
    setConcern(report);
    setParams({ generationAt: String(next) });
  }, [setParams]);
  const navigate = useNavigate();
  const concernAlert = concern === null ? null : (
    <AppAlert
      title={concern.title.text}
      titleId={concern.title.code}
      kind="concern"
      buttons={
        concern.next === "takeMeThere"
          ? [
            { label: word("review.concern.takeMeThere"), wordsId: "review.concern.takeMeThere", onPress: () => { setConcern(null); navigate("/details"); } },
            { label: word("review.concern.notNow"), wordsId: "review.concern.notNow", cancel: true, onPress: () => setConcern(null) },
          ]
          : concern.next === "rebuild"
            ? [
              { label: word("review.concern.rebuild"), wordsId: "review.concern.rebuild", onPress: () => { setConcern(null); navigate("/you"); } },
              { label: word("review.concern.notNow"), wordsId: "review.concern.notNow", cancel: true, onPress: () => setConcern(null) },
            ]
            : [{ label: word("review.concern.ok"), wordsId: "review.concern.ok", cancel: true, onPress: () => setConcern(null) }]
      }
    >
      <p className="pre-line" data-concern-message>{concern.message}</p>
      {concern.outcomeLine !== null && <p className="pre-line" data-copy-code={concern.outcomeLine.code}>{concern.outcomeLine.text}</p>}
    </AppAlert>
  );
  const state = useCall(
    () => session.transport.v2GenerateLetter.fetch({ appealId, generationAt }),
    [session.transport, appealId, generationAt, session.revision],
  );
  // The snapshot this call answered with, handed to the rail (WALK-FIX-1, ../../app/railAppeal.ts).
  useReportAppeal(state.status === "ok" ? state.response.appeal : null);
  const resolution = useCall(
    // EVERY CORRECTION RESENT (WEB-PARITY-1C, F-WP1A-1): a bare `record` here put the county's figures back over the
    // owner's on every visit.
    () => recordKeepingCorrections(session.transport, appealId),
    [session.transport, appealId, session.revision],
  );

  if (state.status === "pending") {
    return (
      <>
        <p className="spinner-line" role="status">
          Loading your letter…
        </p>
        {concernAlert}
      </>
    );
  }
  if (state.status === "failed") return <Refusal failure={state.failure} context="Your letter" />;
  const job = state.response.body.job;
  // WEB-RESUME: still being written, by the server's own rule — the overlay, which goes on to this letter when it ships.
  if ((job.status === "queued" || job.status === "generating") && job.inFlight) {
    return <Navigate to={`/building?generationAt=${job.generationAt}`} replace />;
  }
  if (job.status !== "shipped") {
    return (
      <>
        <h1>This letter isn't ready.</h1>
        {/* No status enum in the sentence (WEB-PARITY-1B, LT-12): it stays on the element. */}
        <div className="notrun" data-job-status={job.status}>
          Nothing has been written yet.
        </div>
      </>
    );
  }
  // WEB-RESUME: a NEWER generation the appeal is still waiting on (the snapshot's head is unsettled until the first read
  // of the job's ending), resumed on this page as a change being made.
  const head = state.response.appeal?.letter ?? null;
  const newer = head !== null && head.latestGenerationAt !== null && head.latestGenerationAt > job.generationAt
    && (head.status === "queued" || head.status === "generating")
    ? head.latestGenerationAt
    : null;
  return (
    <>
      <Review appealId={appealId} job={job} snapshot={state.response} resolution={resolution} onRevised={onRevised} resumeAt={newer} />
      {concernAlert}
    </>
  );
}

function Review({
  appealId, job, snapshot, resolution, onRevised, resumeAt,
}: {
  appealId: string; job: Shipped; snapshot: FetchLetterResponse; resolution: CallState<ResolvePropertyResponse>;
  onRevised: (generationAt: number, report: LetterReview["concernReport"]) => void;
  /** A newer generation the appeal is still waiting on (WEB-RESUME), or null. */
  resumeAt: number | null;
}) {
  const session = useSession();
  const navigate = useNavigate();
  const signature = useSignature(appealId);
  // WEB-PARITY-5 (D3): the contact the owner gave in "Your contact info", held by this tab (../../app/contact.ts).
  const contact = useContact();
  const review = job.review;
  const [sheet, setSheet] = useState<null | "change" | "sign" | "feedback" | "pdf" | "contact">(null);
  const [alert, setAlert] = useState<Alert | null>(null);
  const [pdfDoc, setPdfDoc] = useState<{ base64: string; filename: string } | null>(null);
  const [copied, setCopied] = useState<null | "letter" | "summary">(null);
  const [showLetter, setShowLetter] = useState(false);
  const [revising, setRevising] = useState<number | null>(resumeAt);
  useEffect(() => {
    if (resumeAt !== null) setRevising((r) => r ?? resumeAt);
  }, [resumeAt]);
  const deleted = useRef(false);
  const forgetAppeal = useRef(session.forgetAppeal);
  forgetAppeal.current = session.forgetAppeal;
  useEffect(() => () => {
    if (deleted.current) forgetAppeal.current();
  }, []);

  const list = useCall(() => session.transport.v2Outcomes.list(), [session.transport, session.revision]);

  // The signature, and since WEB-PARITY-5 the letterhead given at export (contract 2.23.0): both in the request only.
  const signedRequest = {
    ...(signature === null ? {} : { signature: { pngBase64: signature.pngBase64 } }),
    ...(contact === null ? {} : { letterhead: contact }),
  };
  const pdf = useAction(async (at: number) => {
    const result = await session.transport.v2GenerateLetter.pdf({ appealId, generationAt: at, ...signedRequest });
    // THE SIGNED PDF IS MADE: the tab's stored copy of the signature goes (WALK-FIX-1, ../../app/signature.ts).
    if (result.ok && signature !== null) releaseStoredSignature(appealId);
    return result;
  });
  const remove = useAction(() => session.transport.v2GenerateLetter.deleteAppeal({ appealId }));
  const queue = useAction(async (concernText: string, q: LetterReview["queuedWith"]) => {
    const result = await session.transport.v2GenerateLetter.queue({
      appealId,
      // ONE PER ATTEMPT (WEB-RESUME): a change request is a new generation, never a replay of the last one — and a retry of
      // one whose answer never came back is the same request, never a second.
      idempotencyKey: attemptKey(appealId, "revise"),
      mode: "revise",
      tone: q.tone,
      // The contact given in this tab, as the app sends its current profile with every request (WEB-PARITY-5, D3);
      // without one, the letterhead this letter was queued with.
      owner: contact !== null ? ownerOf(contact) : {
        name: q.owner.name, mailingAddress: q.owner.mailingAddress,
        ...(q.owner.email === null ? {} : { email: q.owner.email }), ...(q.owner.phone === null ? {} : { phone: q.owner.phone }),
      },
      // What the owner attested for this letter, re-sent as the app re-sends its stamps (LetterService.swift:1105-1116).
      attestations: {
        // Every website generation was queued with an adoption; one without could not be revised (the queue refuses it,
        // with its own sentence, and that refusal is what the owner sees).
        adoptAsOwn: q.attestations.adoptAsOwn ?? { variantSha256: "", clientTimestampMs: 0 },
        ...(q.attestations.canRaiseAck === null ? {} : { canRaiseAck: q.attestations.canRaiseAck }),
      },
      revise: { ownerConcern: concernText },
      appVersion: "web/2.22.0",
    });
    attemptAnswered(appealId, "revise", result);
    return result;
  });

  // THE CHANGE REQUEST'S LETTER, polled at the server's pace until it settles (the app waits on the same call).
  useEffect(() => {
    if (revising === null) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const poll = async (): Promise<void> => {
      const result = await session.transport.v2GenerateLetter.fetch({ appealId, generationAt: revising });
      if (!alive) return;
      if (!result.ok) {
        setRevising(null);
        setAlert({ kind: "reviseError", message: word("review.reviseError.network") });
        return;
      }
      const next = result.response.body.job;
      if (next.status === "shipped") {
        setRevising(null);
        onRevised(next.generationAt, next.review?.concernReport ?? null);
        return;
      }
      if (next.status === "failed") {
        setRevising(null);
        setAlert({ kind: "reviseError", message: next.failure.copy.text });
        return;
      }
      // WEB-RESUME: the server no longer counts it as being written — nothing will finish it, so stop waiting and say so.
      if (!next.inFlight) {
        setRevising(null);
        setAlert({ kind: "reviseError", message: null });
        return;
      }
      timer = setTimeout(() => void poll(), next.pollAfterMs);
    };
    void poll();
    return () => {
      alive = false;
      if (timer !== null) clearTimeout(timer);
    };
  }, [revising, appealId, session.transport, onRevised]);

  const caps = snapshot.appeal?.entitlement.caps.revisions ?? null;
  const remaining = caps === null ? 0 : Math.max(0, caps.max - caps.used);
  const summary = list.status === "ok" ? list.response.body.summaries?.find((a) => a.appealId === appealId) ?? null : null;
  const outcome = snapshot.appeal?.outcome ?? null;
  const jurisdiction = resolution.status === "ok" ? resolution.response.body.jurisdiction : null;
  const deadline = jurisdiction?.deadline ?? null;
  const subjectAddress = resolution.status === "ok" ? resolution.response.body.subject?.address?.trim() ?? "" : "";

  // "Add your contact info" (ReviewView.swift:556-564): the letter's own letterhead is incomplete and none has been given
  // in this tab.
  const missingContact = review !== null && !review.letterhead.minimallyComplete && !contactIsComplete(contact);
  // STALE IS THE SERVER'S, AND ONLY THE SERVER'S (WALK-FIX-1, R236): `review.stale`, the letter's inputs
  // against what it was written from. The letterhead given at export NEVER marks the letter stale: it is drawn at PDF
  // time (op `pdf`'s `letterhead`), not written into the letter. WEB-PARITY-5 had added the app's second half here (a
  // name given since the letter was written, ReviewView.swift:42-51), but the website queues its letter with NO name (D3
  // moved contact to export), so the FIRST name the owner typed counted as "changed": Tyler, 2026-10-05, generated his
  // letter, added his contact and signature, and was told the letter was out of date, and "Revise" sent him to step 1.
  // `test/walkFix1.test.tsx` fails if typing or changing a name flips this.
  const stale = review !== null && review.stale;
  const ownerName = contact !== null && contact.name.trim() !== "" ? contact.name.trim() : review?.queuedWith.owner.name ?? "";
  const missingSignature = signature === null;
  const needsSetup = missingContact || missingSignature;

  const exportPdf = async (): Promise<void> => {
    setAlert(null);
    const result = await pdf.run(job.generationAt);
    if (result.ok) {
      setPdfDoc({ base64: result.response.body.pdf.base64, filename: result.response.body.pdf.filename });
      setSheet("pdf");
    }
  };
  // `handleExportTap` → `proceedToExport` (ReviewView.swift:1320-1336): a letter behind its appeal asks first, then the
  // missing contact or signature does, then the PDF.
  const proceedToExport = (): void => {
    if (needsSetup) setAlert({ kind: "export" });
    else void exportPdf();
  };
  const openLetter = (): void => {
    if (stale) setAlert({ kind: "stale" });
    else proceedToExport();
  };
  const copy = (what: "letter" | "summary", text: string): void => {
    void navigator.clipboard?.writeText(text);
    setCopied(what);
    setTimeout(() => setCopied((c) => (c === what ? null : c)), 1500);
  };

  const exportTitle = missingContact && missingSignature
    ? "review.export.title.both" : missingContact ? "review.export.title.contact" : "review.export.title.signature";
  const signedCaption = wordWith("review.signedCaption", {
    date: new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" }),
  });
  const year = summary?.year ?? new Date().getFullYear();
  const signContext = wordWith("sign.context", { year, place: subjectAddress === "" ? word("sign.context.noAddress") : subjectAddress });

  return (
    <div className="rv" data-letter-sha={job.letterSha256} data-generation-at={job.generationAt}>
      <Say id="review.nav" as="h1" />

      {/* ─── Phase 1 ─── */}
      <PhaseHeader n={1} titleId="review.phase1.title" subtitleId="review.phase1.subtitle" />

      <ReviewSection
        data-section="setup"
        data-setup={needsSetup ? "before" : "signature"}
        header={<Say id={needsSetup ? "review.setup.header.before" : "review.setup.header.signature"} />}
        footer={<Say id="review.setup.footer" />}
      >
        {missingContact && (
          <SetupRow titleId="review.setup.contact.title" subtitleId="review.setup.contact.subtitle" onPress={() => setSheet("contact")} />
        )}
        {missingSignature ? (
          <SetupRow titleId="review.setup.sign.title" subtitleId="review.setup.sign.subtitle" onPress={() => setSheet("sign")} />
        ) : (
          <SetupRow titleId="review.setup.signed.title" subtitleId="review.setup.signed.subtitle" done onPress={() => setSheet("sign")} />
        )}
      </ReviewSection>

      {summary?.estimatedReduction != null && (
        <ReviewSection data-section="hero" data-hero footer={<Say id="review.hero.footer" />}>
          <div className="rv-hero">
            <span className="rv-hero-disc" aria-hidden="true">↘</span>
            <div>
              <Say id="review.hero.label" as="small" className="rv-hero-label" />
              <Say id="review.hero.amount" as="b" className="rv-hero-amount" values={{ amount: money(summary.estimatedReduction.amount) }} />
              <Say id="review.hero.percent" as="p" className="rv-hero-sub" bold values={{ percent: summary.estimatedReduction.percent }} />
            </div>
          </div>
        </ReviewSection>
      )}

      <ReviewSection
        data-section="preview"
        data-letter-preview
        footer={
          // ReviewView.swift:967-977: the stale warning first, then an export's failure, else the PDF's own footer.
          stale
            ? <Say id="review.stale.footer" as="p" className="rv-stale" />
            : pdf.state.status === "failed"
              ? <FailureLine failure={pdf.state.failure} />
              : <p data-held-copy="review.pdfFooter">{PDF_FOOTER.text.replace("{legalEntity}", prePayText("assent.entity.name"))}</p>
        }
      >
        {review !== null && (
          <LetterPaper
            paper={review.paper}
            // "Signed · {date}" ONLY WITH A SIGNATURE HELD (WALK-FIX-1): the card said "Signed" over a PDF that went out
            // with a blank line, because the signature had been lost to a reload. Unsigned, the paper says "Not signed
            // yet" (draft C, approved by Tyler 2026-10-05, in place of the app's "—"): the PDF prints a line to sign by hand.
            signatureName={missingSignature ? "" : ownerName}
            signedCaption={missingSignature ? null : signedCaption}
            busy={pdf.state.status === "pending"}
            onOpen={openLetter}
          />
        )}
        {review === null && job.artifacts.pdf.available === false && (
          <div data-pdf-reason={job.artifacts.pdf.reason}><CopyLine copy={job.artifacts.pdf.copy} /></div>
        )}
        {revising !== null && (
          <p className="rv-working" role="status" aria-label={word("review.change.working").replace("…", "")} data-held-copy="review.change.working">
            <span className="dot" aria-hidden="true" /> {word("review.change.working")}
          </p>
        )}
        {/* ONE BUTTON, TWO PATHS (WALK-FIX-4): always here on a written letter, because "Revise from your details" always
            is; the change request inside it is the app's, and while one is being made the sheet says so instead. */}
        <button
          type="button"
          className="rv-change"
          aria-haspopup="dialog"
          data-change-button
          data-held-copy="review.change.title"
          onClick={() => setSheet("change")}
        >
          <ChangeIcon />
          <span className="rv-change-title">{word("review.change.title")}</span>
          <span className="rv-row-chevron" aria-hidden="true">›</span>
        </button>
      </ReviewSection>

      {job.letter.auditFindings.length > 0 && (
        <ReviewSection data-section="audit" data-audit header={<Say id="review.audit.header" />} footer={<Say id="review.audit.footer" />}>
          <ul className="rv-findings">
            {job.letter.auditFindings.map((finding) => <li key={finding}>{finding}</li>)}
          </ul>
        </ReviewSection>
      )}

      {/* THE FILING DEADLINE IS THE RESOLUTION'S. The app's reminder buttons are not here: a website cannot send the
          phone's notification, and D8's reminder half is Tyler's. An UNVERIFIED deadline gets the app's own line, never
          the server's alarm (WEB-COPY-1, item 6). */}
      {deadline !== null && (
        <ReviewSection data-section="deadline" data-deadline header={<Say id="review.deadline.header" />}>
          {deadline.kind !== "unverified" ? (
            <div className="rv-deadline">
              <b data-copy-code={deadline.label.code}>{deadline.label.text}</b>
              <CopyLine copy={deadline.notice} />
            </div>
          ) : (
            <HeldLine id="deadline.unverified" />
          )}
        </ReviewSection>
      )}

      {/* ─── Phase 2 ─── */}
      <PhaseHeader n={2} titleId="review.phase2.title" subtitleId="review.phase2.subtitle" />

      {/* "Revise from your details" is no longer first here: it is path (b) of "I need a change to this letter" above
          (WALK-FIX-4). This phase keeps only the copy-out rows. */}
      <ReviewSection data-section="letter" data-letter header={<Say id="review.letter.header" />} footer={<Say id="review.letter.footer" as="div" className="rv-pre" />}>
        <div className={showLetter ? "rv-doc" : "rv-doc rv-clamp"} data-letter-text>
          {job.letter.text.trim() === "" ? <Say id="review.letter.placeholder" /> : job.letter.text.trim()}
        </div>
        {job.letter.text.trim() !== "" && (
          <button type="button" className="btn quiet" aria-expanded={showLetter} onClick={() => setShowLetter((v) => !v)}
            data-held-copy={showLetter ? "review.letter.hide" : "review.letter.read"}>
            {word(showLetter ? "review.letter.hide" : "review.letter.read")}
          </button>
        )}
        <button type="button" className="rv-row rv-row-plain" disabled={job.letter.text.trim() === ""}
          onClick={() => copy("letter", job.letter.text)} data-held-copy={copied === "letter" ? "review.copied" : "review.letter.copy"}>
          <span className="rv-row-text"><b>{word(copied === "letter" ? "review.copied" : "review.letter.copy")}</b></span>
        </button>
      </ReviewSection>

      <ReviewSection data-section="summary" data-summary header={<Say id="review.summary.header" />} footer={<Say id="review.summary.footer" />}>
        <div className="rv-doc rv-clamp">
          {job.letter.summary.trim() === "" ? <Say id="review.summary.placeholder" /> : job.letter.summary.trim()}
        </div>
        <button type="button" className="rv-row rv-row-plain" disabled={job.letter.summary === ""}
          onClick={() => copy("summary", job.letter.summary)} data-held-copy={copied === "summary" ? "review.copied" : "review.summary.copy"}>
          <span className="rv-row-text"><b>{word(copied === "summary" ? "review.copied" : "review.summary.copy")}</b></span>
        </button>
      </ReviewSection>

      {/* ─── Phase 3 ─── */}
      <PhaseHeader n={3} titleId="review.phase3.title" subtitleId="review.phase3.subtitle" />

      <ReviewSection data-section="howToFile" data-how-to-file header={<Say id="review.file.header" />} footer={<Say id="review.file.footer" />}>
        <dl className="rv-handoff">
          {jurisdiction?.addressee != null && (
            <div data-addressee={jurisdiction.addressee}>
              <Say id="review.file.who" as="b" />
              <span>{jurisdiction.addressee}</span>
            </div>
          )}
          <div>
            <Say id="review.file.how.title" as="b" />
            <Say id="review.file.how.detail" />
          </div>
          {deadline !== null && deadline.kind !== "unverified" && (
            // The app's row gives the date (`dl.phrase`); the server's sentence for it is the deadline's notice.
            <div>
              <Say id="review.file.deadline" as="b" />
              <span data-copy-code={deadline.notice.code}>{deadline.notice.text}</span>
            </div>
          )}
          <div>
            <Say id="review.file.attach" as="b" />
            <ul className="rv-checklist">
              <Say id="review.file.attach.notice" as="span" className="rv-check" />
              <Say id="review.file.attach.ownership" as="span" className="rv-check" />
              <Say id="review.file.attach.form" as="span" className="rv-check" />
            </ul>
          </div>
        </dl>
      </ReviewSection>

      <ReviewSection
        data-section="outcome"
        data-outcome
        header={<Say id="review.outcome.header" />}
        footer={<Say id={outcome === null ? "review.outcome.footer.none" : "review.outcome.footer.some"} />}
      >
        {outcome !== null && (
          <div className="rv-outcome" data-outcome-result={outcome.result}>
            <span className="rv-badge">{heldText(`outcomes.labels.${outcome.result}.title`)}</span>
            {outcome.result === "raised" ? (
              <Say id="review.outcome.increased" as="b" className="rv-raised" />
            ) : capturesFinalValue(outcome.result) && summary?.realizedReduction != null && summary.realizedReduction.amount > 0 ? (
              <Say id="review.outcome.saved" as="b" className="rv-saved" values={{ amount: dollars(summary.realizedReduction.amount) }} />
            ) : null}
          </div>
        )}
        <button type="button" className="rv-row rv-row-plain" onClick={() => navigate("/outcome")}
          data-held-copy={outcome === null ? "review.outcome.log" : "review.outcome.update"}>
          <span className="rv-row-tile" aria-hidden="true">⚑</span>
          <span className="rv-row-text"><b>{word(outcome === null ? "review.outcome.log" : "review.outcome.update")}</b></span>
        </button>
      </ReviewSection>

      {review !== null && review.history.length > 0 && (
        <ReviewSection
          data-section="previous"
          data-previous
          header={<Say id="review.previous.header" />}
          footer={<Say id="review.previous.footer" values={{ n: 8 }} />}
        >
          {review.history.map((h) => (
            <button
              key={h.generationAt}
              type="button"
              className="rv-row rv-row-plain"
              data-history-generation={h.generationAt}
              onClick={async () => {
                const result = await pdf.run(h.generationAt);
                if (result.ok) savePdf(result.response.body.pdf.base64, result.response.body.pdf.filename);
              }}
            >
              <span className="rv-row-tile" aria-hidden="true">▤</span>
              <span className="rv-row-text">
                <b>{new Date(h.generationAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}</b>
                <small>{h.filename}</small>
              </span>
              <span className="rv-row-chevron" aria-hidden="true">⇩</span>
            </button>
          ))}
        </ReviewSection>
      )}

      <ReviewSection data-section="delete" data-delete footer={<Say id="review.delete.footer" />}>
        <button type="button" className="rv-row rv-row-plain rv-danger" data-held-copy="review.delete" onClick={() => setAlert({ kind: "delete" })}>
          <span className="rv-row-tile" aria-hidden="true">🗑</span>
          <span className="rv-row-text"><b>{word("review.delete")}</b></span>
        </button>
        {remove.state.status === "failed" && <FailureLine failure={remove.state.failure} />}
      </ReviewSection>

      {/* ─── Sheets ─── */}
      {sheet === "change" && (
        <ChangeSheet
          // Path (a) only where the app shows its button: a letter with words in it (`feedbackButton`,
          // ReviewView.swift:859-860); all twenty used, the app's "Let's get someone to look at this".
          request={review === null || job.letter.text.trim() === "" ? null : revising !== null ? "working" : () => {
            if (remaining > 0) {
              setSheet("feedback");
            } else {
              setSheet(null);
              setAlert({ kind: "exhausted", max: caps?.max ?? 20 });
            }
          }}
          onRevise={() => {
            setSheet(null);
            navigate("/details");
          }}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet === "sign" && (
        <SignatureSheet
          appealId={appealId}
          context={signContext}
          suggestedName={ownerName}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet === "contact" && <ContactSheet start={contact} onClose={() => setSheet(null)} />}
      {sheet === "feedback" && (
        <FeedbackSheet
          letterText={job.letter.text}
          remaining={remaining}
          onCancel={() => setSheet(null)}
          onSend={async (concernText) => {
            setSheet(null);
            if (review === null) return;
            const queued = await queue.run(concernText, review.queuedWith);
            if (queued.ok) setRevising(queued.response.body.queued.generationAt);
            else {
              setAlert({
                kind: "reviseError",
                message: queued.kind === "v2Error" ? queued.error.error.copy.text : word("review.reviseError.network"),
              });
            }
          }}
        />
      )}
      {sheet === "pdf" && pdfDoc !== null && (
        <PdfSheet
          pdf={pdfDoc}
          onClose={() => setSheet(null)}
          onDownload={() => savePdf(pdfDoc.base64, pdfDoc.filename)}
          email={job.artifacts.email.available
            ? {
              prefill: contact !== null && contact.email.trim() !== "" ? contact.email.trim() : review?.queuedWith.owner.email ?? "",
              send: (to) => session.transport.v2EmailLetter({ appealId, generationAt: job.generationAt, to, ...signedRequest }),
            }
            : null}
        />
      )}

      {/* ─── Alerts ─── */}
      {alert?.kind === "stale" && (
        <AppAlert
          title={word("review.stale.title")}
          titleId="review.stale.title"
          kind="stale"
          buttons={[
            { label: word("review.stale.revise"), wordsId: "review.stale.revise", onPress: () => { setAlert(null); navigate("/details"); } },
            { label: word("review.stale.exportAsIs"), wordsId: "review.stale.exportAsIs", cancel: true, onPress: () => { setAlert(null); proceedToExport(); } },
          ]}
        >
          <Say id="review.stale.message" as="p" />
        </AppAlert>
      )}
      {alert?.kind === "export" && (
        <AppAlert
          title={word(exportTitle)}
          titleId={exportTitle}
          kind="export"
          buttons={[
            ...(missingContact ? [{ label: word("review.export.addContact"), wordsId: "review.export.addContact", onPress: () => { setAlert(null); setSheet("contact"); } }] : []),
            ...(missingSignature ? [{ label: word("review.export.sign"), wordsId: "review.export.sign", onPress: () => { setAlert(null); setSheet("sign"); } }] : []),
            // The app's `role: .destructive` (ReviewView.swift:311): drawn as the one way out that loses something, never
            // as the amber "do this" beside "Sign this appeal" (WALK-FIX-1).
            { label: word("review.export.anyway"), wordsId: "review.export.anyway", destructive: true, onPress: () => void exportPdf() },
            { label: word("review.export.cancel"), wordsId: "review.export.cancel", cancel: true, onPress: () => setAlert(null) },
          ]}
        >
          <Say id="review.export.message" as="p" />
          {/* Unsigned, before "Export without it": what the PDF will do (draft C, approved by Tyler 2026-10-05). */}
          {missingSignature && <p data-held-copy="review.export.unsignedNote">{heldText("review.export.unsignedNote")}</p>}
        </AppAlert>
      )}
      {alert?.kind === "delete" && (
        <AppAlert
          title={word("review.delete.alert.title")}
          titleId="review.delete.alert.title"
          kind="delete"
          buttons={[
            { label: word("review.delete.alert.cancel"), wordsId: "review.delete.alert.cancel", cancel: true, onPress: () => setAlert(null) },
            {
              label: word("review.delete.alert.confirm"),
              wordsId: "review.delete.alert.confirm",
              onPress: async () => {
                setAlert(null);
                const result = await remove.run();
                if (result.ok) {
                  forgetSignature(appealId);
                  // FORGOTTEN ONCE THIS PAGE HAS GONE: forgetting it here would let the route guard around `/letter`
                  // send the owner to `/` before the navigation to their properties lands (it is a transition).
                  deleted.current = true;
                  navigate("/home");
                }
              },
            },
          ]}
        >
          <Say id="review.delete.alert.message" as="p" />
        </AppAlert>
      )}
      {alert?.kind === "exhausted" && (
        <AppAlert
          title={word("review.exhausted.title")}
          titleId="review.exhausted.title"
          kind="exhausted"
          buttons={[
            {
              label: word("review.exhausted.support"),
              wordsId: "review.exhausted.support",
              onPress: () => { setAlert(null); session.leave(`mailto:${SUPPORT_EMAIL}`); },
            },
            { label: word("review.exhausted.notNow"), wordsId: "review.exhausted.notNow", cancel: true, onPress: () => setAlert(null) },
          ]}
        >
          <Say id="review.exhausted.message" as="p" values={{ max: alert.max }} />
        </AppAlert>
      )}
      {alert?.kind === "reviseError" && (
        <AppAlert
          title={word("review.reviseError.title")}
          titleId="review.reviseError.title"
          kind="reviseError"
          buttons={[{ label: word("review.concern.ok"), wordsId: "review.concern.ok", cancel: true, onPress: () => setAlert(null) }]}
        >
          {alert.message !== null && <p>{alert.message}</p>}
        </AppAlert>
      )}
    </div>
  );
}
